/**
 * BiomarkersSubTab.tsx
 *
 * Biomarkers display matching legacy BIOMARKERS tab.
 * Row click opens history popup. Advanced filtering with multi-token search.
 */
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPatientBiomarkers, type BiomarkerResult } from '@/services/labs.service';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { parseSearchQuery, textMatchesTokens, normalizeLabCompanyName } from './labSearchUtils';
import { TestHistoryPopup } from './TestHistoryPopup';
import { DatePicker } from '@/components/ui/DatePicker';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value + 'T00:00:00');
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface BiomarkersSubTabProps {
  patientId: number;
  onViewInHistory?: (testName: string) => void;
}

export function BiomarkersSubTab({ patientId, onViewInHistory }: BiomarkersSubTabProps) {
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [popupTest, setPopupTest] = useState<string | null>(null);
  const [popupRect, setPopupRect] = useState<DOMRect | null>(null);

  const { data: results, isLoading } = useQuery({
    queryKey: ['patient-biomarkers', patientId],
    queryFn: () => getPatientBiomarkers(patientId),
    staleTime: 5 * 60 * 1000,
  });

  const allResults = results ?? [];

  const historyByTest = useMemo(() => {
    const map = new Map<string, BiomarkerResult[]>();
    for (const r of allResults) {
      const arr = map.get(r.testName) ?? [];
      arr.push(r);
      map.set(r.testName, arr);
    }
    return map;
  }, [allResults]);

  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const r of allResults) set.add(r.source);
    return Array.from(set).sort();
  }, [allResults]);

  const filtered = useMemo(() => {
    let rows = allResults;

    if (search.trim()) {
      const { includeTokens, excludeTokens } = parseSearchQuery(search);
      rows = rows.filter((r) => textMatchesTokens(r.testName + ' ' + r.value, includeTokens, excludeTokens));
    }

    if (sourceFilter) {
      const normalizedFilter = normalizeLabCompanyName(sourceFilter);
      rows = rows.filter((r) => normalizeLabCompanyName(r.source) === normalizedFilter);
    }

    if (dateFrom) rows = rows.filter((r) => r.date >= dateFrom);
    if (dateTo) rows = rows.filter((r) => r.date <= dateTo);

    if (sortCol !== null) {
      const getVal = (r: BiomarkerResult) => {
        switch (sortCol) {
          case 0: return r.date;
          case 1: return r.testName;
          case 2: return r.value;
          case 4: return r.source;
          default: return '';
        }
      };
      rows = [...rows].sort((a, b) => {
        const va = getVal(a) ?? '';
        const vb = getVal(b) ?? '';
        const cmp = sortCol === 0 ? va.localeCompare(vb) : va.localeCompare(vb, undefined, { sensitivity: 'base' });
        return sortAsc ? cmp : -cmp;
      });
    }

    return rows;
  }, [allResults, search, sourceFilter, dateFrom, dateTo, sortCol, sortAsc]);

  function handleSort(col: number) {
    if (sortCol === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(col === 0 ? false : true);
    }
  }

  function sortIndicator(col: number) {
    if (sortCol !== col) return ' ⇅';
    return sortAsc ? ' ↑' : ' ↓';
  }

  const handleRowClick = useCallback((testName: string, e: React.MouseEvent<HTMLTableRowElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (popupTest === testName) {
      setPopupTest(null);
      setPopupRect(null);
    } else {
      setPopupTest(testName);
      setPopupRect(rect);
    }
  }, [popupTest]);

  function handleClearFilters() {
    setSearch('');
    setSourceFilter('');
    setDateFrom('');
    setDateTo('');
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search biomarkers..."
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm w-48 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="">All Sources</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <DatePicker size="sm" ariaLabel="From date" value={dateFrom} onChange={setDateFrom} wrapperClassName="w-36" />
        <span className="text-xs text-slate-400">to</span>
        <DatePicker size="sm" ariaLabel="To date" value={dateTo} onChange={setDateTo} wrapperClassName="w-36" />
        {(search || sourceFilter || dateFrom || dateTo) && (
          <Button size="sm" variant="ghost" onClick={handleClearFilters}>Clear</Button>
        )}
        <span className="text-xs text-slate-400">{filtered.length} results</span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <p className="py-4 text-sm text-slate-400 italic">
          {allResults.length === 0 ? 'No biomarker data found for this patient.' : 'No results match your search.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort(0)}>Date{sortIndicator(0)}</th>
                <th className="px-4 py-3 w-[40%] cursor-pointer select-none" onClick={() => handleSort(1)}>Test{sortIndicator(1)}</th>
                <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort(2)}>Value{sortIndicator(2)}</th>
                <th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort(4)}>Source{sortIndicator(4)}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r, i) => (
                <tr
                  key={`${r.resultSource}-${r.resultId}-${r.testName}-${i}`}
                  className={`cursor-pointer transition-colors ${popupTest === r.testName ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                  onClick={(e) => handleRowClick(r.testName, e)}
                >
                  <td className="px-4 py-3 text-slate-700">{formatDate(r.date)}</td>
                  <td className="px-4 py-3 text-slate-800 font-medium [overflow-wrap:anywhere] min-w-0">{r.testName}</td>
                  <td className="px-4 py-3 text-slate-900 font-semibold">{r.value}</td>
                  <td className="px-4 py-3 text-slate-500">{r.referenceRange ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {popupTest && popupRect && (
        <TestHistoryPopup
          testName={popupTest}
          history={historyByTest.get(popupTest) ?? []}
          anchorRect={popupRect}
          onClose={() => { setPopupTest(null); setPopupRect(null); }}
          onViewInChart={(name) => {
            setPopupTest(null);
            setPopupRect(null);
            onViewInHistory?.(name);
          }}
        />
      )}
    </div>
  );
}
